import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { isEmpty } from 'lodash';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  EStoryLists,
  IExtendedStoryPrivilege,
  IPublicationViewMessages,
  IStoriesFolder,
  IStoryListMessages,
  IStoryListSelection,
  ISubjectClaims
} from '../../../../types';
import { CreateFolderModal } from '../CreateFolderModal';
import { styles } from './Sidebar.styles';
import {
  STORIES_ADMIN_PRIVILEGE,
  STORIES_EDITOR_PRIVILEGE,
  VIEW_SECTIONS_PRIVILEGE
} from '@opengov/stories-shared/constants';
import { hasPrivilege } from '../../../utils';
import { IFolderNodeWithPath, SidebarContextProvider, useSidebarContext } from './SidebarContext';

export type ISidebarProps = {
  copyPublicationJobId?: string;
  isPublicationsTabSelected?: boolean;
  isPublicationFoldersLoading?: boolean;
  currentFolderId: string;
  entityId: number;
  folderStructure: IStoriesFolder[];
  isAdmin: boolean;
  entitySubdomain: string;
  messages: IStoryListMessages;
  rootFolderId: string;
  isOrganizeStories?: boolean;
  isOrganizeSections?: boolean;
  isOrganizeStoriesInProgress?: boolean;
  publicationViewMessages?: IPublicationViewMessages;
  selectedList?: IStoryListSelection;
  showReports?: boolean;
  selectedSectionName?: string;
  storiesAccessControlEnabled?: boolean;
  currentSubjectClaims?: ISubjectClaims | null;
  currentSubjectPublicationStoryClaim?: IExtendedStoryPrivilege | null;
  isCustomSpreadsheetReportEnabled?: boolean;
  setSelectedList?(newList: IStoryListSelection, entitySubdomain: string, isOrganizeStories?: boolean): void;
  setSelectedSection?(section: IStoryListSelection | null, view?: string): void;
  setIsCreatePublicationModalVisible?(isCreatePublicationModalVisible: boolean): void;
  setIsReorderRootSections?(value: boolean): void;
};

export const STORIES = 'stories';
export const TEMPLATES = 'templates';
export const PUBLICATIONS = 'publications';
export const REPORTS = 'embedded-reports';
export const CUSTOM_REPORTS = 'custom-reports';

type NavItemDef = { label: string; dataTest: string; key: string; icon: React.ReactNode };

const getNavItemDefs = ({
  isOrganizeStories,
  isAdmin,
  isPublicationsTabSelected,
  messages,
  publicationViewMessages,
  showReports,
  isCustomSpreadsheetReportEnabled,
  currentSubjectClaims
}: {
  isOrganizeStories: boolean;
  isAdmin: boolean;
  isPublicationsTabSelected: boolean;
  messages: IStoryListMessages;
  publicationViewMessages: IPublicationViewMessages;
  showReports?: boolean;
  isCustomSpreadsheetReportEnabled?: boolean;
  currentSubjectClaims: ISubjectClaims | null;
}): NavItemDef[] => {
  if (isOrganizeStories) return [];

  if (isAdmin && !isPublicationsTabSelected) {
    return [
      { label: messages.viewAllStories, dataTest: 'view-all-stories', key: STORIES, icon: <GridViewOutlinedIcon fontSize="small" /> },
      { label: messages.viewAllTemplates, dataTest: 'view-all-templates', key: TEMPLATES, icon: <ArticleOutlinedIcon fontSize="small" /> }
    ];
  }

  if (isPublicationsTabSelected) {
    const items: NavItemDef[] = [
      { label: publicationViewMessages.allPublicationStories, dataTest: 'view-all-publications', key: PUBLICATIONS, icon: <GridViewOutlinedIcon fontSize="small" /> }
    ];
    if (showReports && isAdmin) {
      items.push({ label: publicationViewMessages.allPublicationReports, dataTest: 'view-all-reports', key: REPORTS, icon: <BarChartOutlinedIcon fontSize="small" /> });
    }
    if (isCustomSpreadsheetReportEnabled && (isAdmin || hasPrivilege(currentSubjectClaims, STORIES_EDITOR_PRIVILEGE))) {
      items.push({ label: publicationViewMessages.customReports, dataTest: 'view-all-custom-reports', key: CUSTOM_REPORTS, icon: <TableChartOutlinedIcon fontSize="small" /> });
    }
    return items;
  }

  return [{ label: messages.viewAllStories, dataTest: 'view-all-stories', key: PUBLICATIONS, icon: <GridViewOutlinedIcon fontSize="small" /> }];
};

// Exported for tests — returns JSX with data-test attributes so existing test assertions pass
export const getBlockListItems = (args: {
  isOrganizeStories: boolean;
  isAdmin: boolean;
  isPublicationsTabSelected: boolean;
  messages: IStoryListMessages;
  publicationViewMessages: IPublicationViewMessages;
  showReports?: boolean;
  isCustomSpreadsheetReportEnabled?: boolean;
  currentSubjectClaims: ISubjectClaims | null;
}): React.ReactNode[] =>
  getNavItemDefs(args).map((item) => (
    <span key={item.key} data-test={item.dataTest}>{item.label}</span>
  ));

type FolderTreeProps = {
  nodes: IFolderNodeWithPath[];
  activeNodeId: string;
  depth?: number;
  isFiltering?: boolean;
  onNodeSelect: (node: IStoriesFolder) => void;
  onToggle: (node: IStoriesFolder, newToggled: boolean) => void;
};

const FolderTree = ({ nodes, activeNodeId, depth = 0, isFiltering = false, onNodeSelect, onToggle }: FolderTreeProps) => (
  <List disablePadding>
    {nodes.map((node) => (
      <React.Fragment key={node.id}>
        <ListItemButton
          id={`${node.id}-node`}
          selected={node.id === activeNodeId}
          onClick={() => onNodeSelect(node)}
          sx={styles.treeNodeButton}
          style={isFiltering ? undefined : { paddingLeft: `${depth * 16 + 8}px` }}
        >
          {!isFiltering && (
            node.children && node.children.length > 0 ? (
              <ListItemIcon sx={styles.treeToggleIcon}>
                <IconButton
                  size="small"
                  role="button"
                  data-test={`${node.id}-toggle`}
                  aria-label={node.toggled ? 'Collapse' : 'Expand'}
                  onClick={(e) => { e.stopPropagation(); onToggle(node, !node.toggled); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggle(node, !node.toggled); } }}
                  sx={styles.treeToggleIconButton}
                >
                  {node.toggled ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              </ListItemIcon>
            ) : (
              <ListItemIcon sx={styles.treeToggleIconSpacer}>
                <Box component="span" aria-hidden="true" sx={{ display: 'none' }} />
              </ListItemIcon>
            )
          )}
          <Tooltip title={node.displayName} placement="bottom" enterDelay={500}>
            <ListItemText primary={node.displayName} sx={styles.treeNodeText} />
          </Tooltip>
        </ListItemButton>
        {node.children && node.children.length > 0 && (
          <Collapse in={!!node.toggled} timeout="auto" unmountOnExit>
            <FolderTree
              nodes={node.children ?? []}
              activeNodeId={activeNodeId}
              depth={depth + 1}
              isFiltering={isFiltering}
              onNodeSelect={onNodeSelect}
              onToggle={onToggle}
            />
          </Collapse>
        )}
      </React.Fragment>
    ))}
  </List>
);

export const SidebarComponent: React.FC<ISidebarProps> = (props) => {
  const {
    copyPublicationJobId,
    isPublicationsTabSelected = false,
    currentFolderId,
    entityId,
    entitySubdomain,
    isAdmin,
    messages,
    rootFolderId,
    selectedList,
    publicationViewMessages,
    folderStructure,
    isOrganizeStories = false,
    isPublicationFoldersLoading,
    isOrganizeSections,
    isOrganizeStoriesInProgress,
    selectedSectionName,
    storiesAccessControlEnabled,
    currentSubjectClaims,
    currentSubjectPublicationStoryClaim,
    isCustomSpreadsheetReportEnabled,
    setSelectedSection,
    setSelectedList,
    setIsCreatePublicationModalVisible,
    setIsReorderRootSections,
    showReports
  } = props;

  const { data, filterValue, setFilterValue } = useSidebarContext();
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);

  // localData mirrors context data but preserves local toggled state across re-renders
  const [localData, setLocalData] = useState<IFolderNodeWithPath[]>(data);

  const localToggleMapRef = useRef<Record<string, boolean>>({});
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [sidebarMaxHeight, setSidebarMaxHeight] = React.useState<string>('calc(100vh - 200px)');

  React.useEffect(() => {
    const updateHeight = () => {
      if (sidebarRef.current) {
        const offsetTop = sidebarRef.current.getBoundingClientRect().top;
        setSidebarMaxHeight(`calc(100vh - ${offsetTop + 24}px)`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(document.body);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Sync localData whenever context data changes
  React.useEffect(() => {
    const mergeToggled = (nodes: IFolderNodeWithPath[]): IFolderNodeWithPath[] =>
      nodes.map((node) => ({
        ...node,
        toggled: localToggleMapRef.current[node.id] !== undefined
          ? localToggleMapRef.current[node.id]
          : !!node.toggled,
        children: node.children ? mergeToggled(node.children) : node.children
      }));
    setLocalData(mergeToggled(data));
  }, [data]);

  const navItems = getNavItemDefs({
    isOrganizeStories,
    isAdmin,
    isPublicationsTabSelected,
    messages,
    publicationViewMessages: publicationViewMessages!,
    showReports,
    isCustomSpreadsheetReportEnabled,
    currentSubjectClaims: currentSubjectClaims ?? null
  });

  const selectedIndexChangedAction = (index: number): void => {
    if (isPublicationsTabSelected) {
      if (index === 0) setSelectedSection?.(null, STORIES);
      else if (index === 1) setSelectedSection?.(null, REPORTS);
      else if (index === 2) setSelectedSection?.(null, CUSTOM_REPORTS);
    } else {
      if (index === 0) {
        setSelectedList?.({ id: EStoryLists.Stories, displayName: messages.allStories }, entitySubdomain);
      } else if (index === 1) {
        setSelectedList?.({ id: EStoryLists.Templates, displayName: messages.allTemplates }, entitySubdomain);
      }
    }
  };

  const blockListIndex = (): number => {
    if (isPublicationsTabSelected && isEmpty(currentFolderId)) {
      switch (selectedSectionName) {
        case REPORTS: return 1;
        case CUSTOM_REPORTS: return 2;
        default: return 0;
      }
    }
    switch (selectedList?.id) {
      case EStoryLists.Stories: return 0;
      case EStoryLists.Templates: return 1;
      default: return 100;
    }
  };

  const handleNodeSelect = (node: IStoriesFolder) => {
    if (isPublicationsTabSelected) {
      setSelectedSection?.({ id: node.id, displayName: node.displayName });
    } else {
      setSelectedList?.({ id: node.id, displayName: node.displayName }, entitySubdomain, isOrganizeStories);
    }
  };

  const handleToggle = (targetNode: IStoriesFolder, newToggled: boolean) => {
    localToggleMapRef.current[targetNode.id] = newToggled;
    const updateToggled = (nodes: IStoriesFolder[]): IStoriesFolder[] =>
      nodes.map((n) =>
        n.id === targetNode.id
          ? { ...n, toggled: newToggled }
          : { ...n, children: n.children ? updateToggled(n.children) : n.children }
      );
    setLocalData((prev) => updateToggled(prev));
  };

  const foldersMatching = () => (
    <Box>
      <Box sx={styles.searchResults}>
        {data.length > 0 ? messages.folderResults({ numberOfFolders: data.length }) : messages.noFoldersMatching}
        <Box
          component="span"
          data-test="clear-filter-button"
          role="button"
          tabIndex={0}
          sx={styles.clearFilter}
          onClick={() => setFilterValue('')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFilterValue(''); }}
        >
          {messages.clearFilter}
        </Box>
      </Box>
      <Divider />
    </Box>
  );

  const addFolderButton = () => {
    if (isPublicationsTabSelected && (!folderStructure?.length || isPublicationFoldersLoading)) return null;
    if (!isAdmin || isOrganizeStories) return null;
    return (
      <Button
        variant="text"
        size="small"
        data-test="add-folder-button"
        sx={[styles.addFolderButton, copyPublicationJobId ? styles.hideControls : {}]}
        onClick={() => {
          if (!isPublicationsTabSelected) {
            setIsCreateFolderModalVisible(true);
          } else {
            setIsCreatePublicationModalVisible?.(true);
          }
        }}
      >
        {!isPublicationsTabSelected ? messages.addFolder : publicationViewMessages?.addSection ?? ''}
      </Button>
    );
  };

  const addReorderButton = () => (
    <Button
      variant="outlined"
      size="small"
      disabled={isOrganizeStoriesInProgress}
      data-test="reorder-root-sections-button"
      onClick={() => setIsReorderRootSections?.(true)}
    >
      {publicationViewMessages?.reorder ?? ''}
    </Button>
  );

  const organizeStoriesAndFoldersShouldShowContent =
    !storiesAccessControlEnabled ||
    (currentSubjectClaims && hasPrivilege(currentSubjectClaims, STORIES_ADMIN_PRIVILEGE));
  const shouldShowContent =
    !storiesAccessControlEnabled ||
    (Array.isArray(currentSubjectPublicationStoryClaim) &&
      currentSubjectPublicationStoryClaim.includes(VIEW_SECTIONS_PRIVILEGE));

  const appElement = document.getElementById('app');

  return (
    <Box ref={sidebarRef} component="nav" aria-label="Publication sections" sx={{ ...styles.sidebar, maxHeight: sidebarMaxHeight }}>
      {isCreateFolderModalVisible &&
        appElement &&
        createPortal(
          <CreateFolderModal
            currentFolderId={currentFolderId}
            rootFolderId={rootFolderId}
            folderStructure={folderStructure}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            messages={messages}
            onClose={() => setIsCreateFolderModalVisible(false)}
          />,
          appElement
        )}
      {(organizeStoriesAndFoldersShouldShowContent || shouldShowContent || !isPublicationsTabSelected) && (
        <>
          {navItems.length > 0 && (
            <>
              <List disablePadding data-test="view-all-options">
                {navItems.map((item, index) => (
                  <ListItemButton
                    key={item.key}
                    data-test={`list-item-${index}`}
                    selected={blockListIndex() === index}
                    aria-current={blockListIndex() === index ? 'page' : undefined}
                    sx={styles.navItemButton}
                    onClick={() => selectedIndexChangedAction(index)}
                  >
                    <ListItemIcon sx={styles.navItemIcon}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                    />
                  </ListItemButton>
                ))}
              </List>
              <Divider />
            </>
          )}
          <Box sx={styles.filterWrapper}>
            <Box
              sx={[
                styles.filterHeader,
                !(isOrganizeStories || isOrganizeSections) ? styles.titlePadding : {}
              ]}
            >
              <Box sx={styles.flexRow}>
                {isPublicationsTabSelected
                  ? <AccountTreeOutlinedIcon sx={styles.folderIcon} />
                  : <FolderOutlinedIcon sx={styles.folderIcon} />
                }
                <Typography component="h2" sx={styles.foldersHeader}>
                  {!isPublicationsTabSelected ? messages.folders : publicationViewMessages?.sections ?? ''}
                </Typography>
              </Box>
              {isOrganizeSections ? addReorderButton() : addFolderButton()}
            </Box>
            {!isOrganizeStories && (
              <TextField
                size="small"
                fullWidth
                id="sidebar-search"
                inputProps={{ 'data-test': 'filter-input-text', className: 'no-format', 'aria-label': isPublicationsTabSelected ? publicationViewMessages?.searchSections ?? 'Search sections' : messages.filterFolders }}
                placeholder={
                  isPublicationsTabSelected
                    ? publicationViewMessages?.searchSections ?? ''
                    : messages.filterFolders
                }
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isPublicationsTabSelected
                        ? <SearchIcon fontSize="small" />
                        : <Typography variant="caption">{messages.filter}</Typography>
                      }
                    </InputAdornment>
                  ),
                  endAdornment: filterValue ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="clear filter"
                        onClick={() => setFilterValue('')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            {!isOrganizeStories && filterValue !== '' && foldersMatching()}
          </Box>
          {isOrganizeStories && <Divider />}
          {localData.length !== 0 && (
            <Box sx={styles.scrollContainer} data-test="folders-list">
              <Box sx={styles.scrollInner}>
                <FolderTree
                  nodes={localData}
                  activeNodeId={currentFolderId}
                  isFiltering={filterValue !== ''}
                  onNodeSelect={handleNodeSelect}
                  onToggle={handleToggle}
                />
              </Box>
            </Box>
          )}
          {localData.length === 0 && isPublicationsTabSelected && !isPublicationFoldersLoading && (
            <Box sx={styles.noSectionsView} data-test="no-sections-view">
              <ListAltOutlinedIcon sx={styles.folderIcon} />
              <Typography variant="body2" color="text.secondary">
                {publicationViewMessages?.noSections ?? ''}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const Sidebar: React.FC<ISidebarProps> = (props) => {
  const applyToggledState = (nodes: IStoriesFolder[]): IStoriesFolder[] =>
    nodes.map((node) => ({
      ...node,
      toggled: !!node.toggled,
      children: node.children ? applyToggledState(node.children as IStoriesFolder[]) : node.children
    }));

  const [folderStructure, setFolderStructure] = useState<IStoriesFolder[]>(() =>
    applyToggledState(props.folderStructure)
  );

  React.useEffect(() => {
    setFolderStructure(applyToggledState(props.folderStructure));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.folderStructure)]);

  return (
    <SidebarContextProvider data={folderStructure}>
      <SidebarComponent {...props} />
    </SidebarContextProvider>
  );
};
